import { Box, Divider, Flex, Stack, Text } from '@chakra-ui/react';
import { isEmpty, omitBy } from 'lodash';
import { useCallback, useState } from 'react';

import { DebouncedNumberInput } from '../DebouncedNumberInput';
import { SegmentedControls } from '../SegmentedControls';

export enum NumberFilterMode {
    AND = 'AND',
    OR = 'OR',
}

export interface NumberFilterValue {
    eq?: string;
    gt?: string;
    gte?: string;
    lt?: string;
    lte?: string;
    mode?: NumberFilterMode;
}
export const NumberFilter = ({
    filterValue,
    setFilterValue,
}: {
    filterValue?: NumberFilterValue;
    setFilterValue: (filterValue?: NumberFilterValue) => void;
}) => {
    const onChange = useCallback(
        (change: NumberFilterValue) => {
            const merged = omitBy(
                {
                    ...filterValue,
                    ...change,
                },
                isEmpty,
            );
            const { mode, ...operators } = merged;
            setFilterValue(Object.keys(operators).length ? merged : undefined);
        },
        [filterValue, setFilterValue],
    );

    const [lowerBoundary, setLowerBoundary] = useState<'gt' | 'gte'>(filterValue?.gte?.length ? 'gte' : 'gt');
    const [upperBoundary, setUpperBoundary] = useState<'lt' | 'lte'>(filterValue?.lte?.length ? 'lte' : 'lt');

    const hasRangeOperator =
        !!filterValue?.gt?.length ||
        !!filterValue?.gte?.length ||
        !!filterValue?.lt?.length ||
        !!filterValue?.lte?.length;

    return (
        <Stack gap={1}>
            <Flex alignItems="center" justifyContent="space-between">
                <Text>Equals</Text>
                <DebouncedNumberInput
                    initialValue={filterValue?.eq}
                    debounceCallback={(value) => onChange({ eq: value })}
                    disabled={hasRangeOperator}
                />
            </Flex>
            <Divider my={2} />
            <Flex alignItems="center" justifyContent="space-between">
                <Box width="70px">
                    <SegmentedControls
                        stretch
                        variant="solid"
                        size="xs"
                        options={[
                            { label: 'GT', value: 'gt' },
                            { label: 'GTE', value: 'gte' },
                        ]}
                        onClick={(value) => {
                            setLowerBoundary((current) => {
                                onChange({ [value]: filterValue?.[current], [current]: undefined });
                                return value;
                            });
                        }}
                        selectedValue={lowerBoundary}
                    />
                </Box>
                <DebouncedNumberInput
                    initialValue={filterValue?.[lowerBoundary]}
                    debounceCallback={(value) => onChange({ [lowerBoundary]: value })}
                    disabled={!!filterValue?.eq?.length}
                />
            </Flex>
            <Flex alignItems="center" justifyContent="space-between">
                <Box width="70px">
                    <SegmentedControls
                        stretch
                        variant="solid"
                        size="xs"
                        options={[
                            { label: 'LT', value: 'lt' },
                            { label: 'LTE', value: 'lte' },
                        ]}
                        onClick={(value) => {
                            setUpperBoundary((current) => {
                                onChange({ [value]: filterValue?.[current], [current]: undefined });
                                return value;
                            });
                        }}
                        selectedValue={upperBoundary}
                    />
                </Box>
                <DebouncedNumberInput
                    initialValue={filterValue?.[upperBoundary]}
                    debounceCallback={(value) => onChange({ [upperBoundary]: value })}
                    disabled={!!filterValue?.eq?.length}
                />
            </Flex>
            <Divider my={2} />
            <Flex alignItems="center" justifyContent="space-between" gap={32}>
                <Text>Mode</Text>
                <SegmentedControls
                    stretch
                    variant="solid"
                    size="xs"
                    options={[
                        {
                            label: NumberFilterMode.AND,
                            value: NumberFilterMode.AND,
                            disabled: !hasRangeOperator || !!filterValue?.eq,
                        },
                        {
                            label: NumberFilterMode.OR,
                            value: NumberFilterMode.OR,
                            disabled: !hasRangeOperator || !!filterValue?.eq,
                        },
                    ]}
                    onClick={(value) => onChange({ mode: value })}
                    selectedValue={filterValue?.mode ?? NumberFilterMode.AND}
                />
            </Flex>
        </Stack>
    );
};
